import { AuthStorage } from '@/utils/storage/AuthStorage'
import { ETC_OUTBOUND_MENU_CD, ETC_OUTBOUND_SEARCH_MENU_CD } from '@/services/outbound/outboundEtc/consts'
import type { EtcOutboundListBody } from '@/services/outbound/outboundEtc/useOutboundEtcQueries'
import {
  EtcOutboundConfirmBody,
  EtcOutboundEditableRow,
  EtcOutboundRow,
  EtcOutboundSaveBody,
  EtcOutboundSaveItem,
  EtcOutboundSavedItem,
  EtcOutboundSearchParams,
} from '@/services/outbound/outboundEtc/types'

/** 조회 조건. 1.0 `GI35Container.handleSearch` 가 만들던 본문에서 구간(page·pageSize·type)을 뺀 것 — 구간은 전량 조회 훅이 채운다(LOGISPRD-15268). */
export const toSearchBody = (params: EtcOutboundSearchParams): EtcOutboundListBody => ({
  MENU_CD: ETC_OUTBOUND_SEARCH_MENU_CD,
  USR_ID: AuthStorage.user.get().email ?? '',
  params,
})

/** 조회 응답 행 → 표 행. 행 키는 주문일련번호 기준이고, 없으면 인덱스로 보완한다. */
export const toEditableRows = (rows: EtcOutboundRow[]): EtcOutboundEditableRow[] =>
  rows.map((row, index) => ({
    ...row,
    key: `${row.ORD_NO}-${row.ORD_OPT_NO}-${index}`,
    // API 는 boolean, 표는 Y/N. 여기서 한 번만 바꾼다.
    IF_REQ: row.IF_REQ ? 'Y' : 'N',
  }))

/** 표 행 → 저장 항목. 표 로컬 필드(key)와 표시 전용 SKU_ID 는 저장 계약에 없어 빼고, EDITTED 를 얹는다. */
export const toSaveItem = (row: EtcOutboundEditableRow): EtcOutboundSaveItem => {
  const item: Record<string, unknown> = { ...row }
  delete item.key
  delete item.SKU_ID

  return {
    ...(item as Omit<EtcOutboundEditableRow, 'key' | 'SKU_ID'>),
    // 'U' 가 아니라 'E' 다 — BE 가 isUpdate() 를 editted == "E" 로 본다. types.ts 주석 참고.
    EDITTED: row.EDITTED ?? 'E',
    // BE `ProcessEtcOutboundOrderItem` 은 IF_REQ 를 boolean 으로 받는다. 표는 Y/N 이다.
    IF_REQ: String(row.IF_REQ).toUpperCase() !== 'N',
    // 편집 셀에서 온 수량은 문자열이다. BE `quantity` 는 Int 라 숫자로 바꿔 보낸다.
    ORD_QTY: Number(row.ORD_QTY) || 0,
    // BE `ProcessEtcOutboundDataContent.shipperCode` 는 non-null 이다. 1.0 은 ShipperUtils 가 채웠다.
    shipper_code: row.SPR_NM,
  }
}

interface SaveBodyParams {
  /** 일반 저장은 항상 `EtcOutboundSaveItem[]`. 중복 확인 후 강행 저장은 BE 응답 항목이 섞일 수 있다. */
  items: (EtcOutboundSaveItem | EtcOutboundSavedItem)[]
  shipperCode: string
  shipperName: string
  /** 중복 확인 후 강행 저장일 때만 채운다. 있으면 BE 가 중복 검사를 건너뛴다. */
  remark?: string
}

/**
 * 저장 요청 본문.
 *
 * 일반 저장은 `headData`, 중복 확인 후 강행은 `itemData` + `subData` 다 —
 * BE `ProcessEtcOutboundDataContent.toCommand` 가 `itemData` 유무로 중복 검사를 가른다.
 */
export const toSaveBody = ({ items, shipperCode, shipperName, remark }: SaveBodyParams): EtcOutboundSaveBody => {
  const user = AuthStorage.user.get()
  return {
    MENU_CD: ETC_OUTBOUND_MENU_CD,
    ITEM_CD: ETC_OUTBOUND_MENU_CD,
    USR_ID: user.email ?? '',
    SPR_NM: shipperCode,
    DEPT_NM: user.DEPT_NM ?? '',
    shipper_code: shipperCode,
    shipper_name: shipperName,
    data: remark ? { itemData: items, subData: [{ REMARK: remark, EDITTED: 'I' }] } : { headData: items },
  }
}

/** 확정 요청 본문. 선택한 행의 (화주코드·주문번호·주문일련번호)만 보낸다. */
export const toConfirmBody = (rows: EtcOutboundEditableRow[], shipperCode: string): EtcOutboundConfirmBody => ({
  params: { SPR_NM: shipperCode, MENU_CD: ETC_OUTBOUND_MENU_CD },
  data: rows.map((row) => ({ SPR_NM: row.SPR_NM, ORD_NO: row.ORD_NO, ORD_OPT_NO: row.ORD_OPT_NO })),
})
